import { ExternalLink, Mail, BookOpen, MessageCircle, Zap, ShoppingBag, ScanBarcode, Tag } from 'lucide-react';

const FAQS = [
  {
    q: 'How do I connect my eBay account?',
    a: 'Go to Settings → Platforms and click "Connect" next to eBay. You\'ll be redirected to eBay to authorise Crosova to access your seller account.',
  },
  {
    q: 'How does auto-delist work?',
    a: 'When an item sells on any platform, Crosova automatically removes it from all other connected platforms within seconds — preventing overselling.',
  },
  {
    q: 'How does Scout work?',
    a: 'Scout lets you look up live eBay UK sold prices before buying an item to resell. Enter a barcode or upload a photo — Scout identifies the item and shows you real sold prices.',
  },
  {
    q: 'What is the Price Advisor?',
    a: 'The Price Advisor uses AI to suggest a realistic selling price based on your item details. For live market data, use Scout instead.',
  },
  {
    q: 'How do I schedule a listing to go live later?',
    a: 'In the new listing wizard, go to the Review step and toggle "Schedule for later". Pick a date and time — Crosova will publish it automatically.',
  },
  {
    q: 'What are shipping policies?',
    a: 'Shipping policies let you save your standard shipping and returns settings (e.g. "Royal Mail 2nd Class, £2.99, 30-day returns") and apply them to listings in one click.',
  },
  {
    q: 'How does the listing health score work?',
    a: 'In the listing wizard Review step, click "Check quality" — AI analyses your title, description, photos, price and tags, then gives a score and suggestions.',
  },
  {
    q: 'Can I import my existing eBay listings?',
    a: 'Yes. Go to Import in the sidebar, select eBay, and Crosova will pull in all your existing listings as drafts with photos and details preserved.',
  },
];

const GUIDES = [
  { icon: ShoppingBag, title: 'Creating your first listing', desc: 'Step-by-step guide to adding an item and crosslisting it' },
  { icon: ScanBarcode, title: 'Using Scout to price items', desc: 'How to use barcode and photo scanning in store' },
  { icon: Zap, title: 'Setting up auto-delist', desc: 'Configure automatic removal when items sell' },
  { icon: Tag, title: 'AI tools overview', desc: 'Titles, descriptions, tags, price advisor and health score' },
];

export function HelpPage() {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-base font-semibold tracking-tight">Help & support</h1>
        <p className="text-[11.5px] text-white/25 mt-0.5">Guides, FAQs and how to get in touch</p>
      </div>

      {/* Contact */}
      <div className="grid grid-cols-2 gap-3">
        <a
          href="mailto:hello@crosova.com"
          className="card p-4 flex items-start gap-3 hover:bg-white/[0.03] transition-colors"
        >
          <div className="w-8 h-8 bg-primary-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Mail size={15} className="text-primary-400" />
          </div>
          <div>
            <div className="text-[13px] font-medium">Email support</div>
            <div className="text-[11.5px] text-white/40 mt-0.5">hello@crosova.com</div>
            <div className="text-[10.5px] text-white/25 mt-1">We reply within 24 hours</div>
          </div>
        </a>
        <div className="card p-4 flex items-start gap-3 opacity-50 cursor-not-allowed">
          <div className="w-8 h-8 bg-secondary-400/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <MessageCircle size={15} className="text-secondary-400" />
          </div>
          <div>
            <div className="text-[13px] font-medium">Live chat</div>
            <div className="text-[11.5px] text-white/40 mt-0.5">Coming soon</div>
            <div className="text-[10.5px] text-white/25 mt-1">Available on Pro plan</div>
          </div>
        </div>
      </div>

      {/* Quick guides */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-white/[0.05] flex items-center gap-2">
          <BookOpen size={13} className="text-white/40" />
          <h2 className="text-[12.5px] font-semibold">Quick guides</h2>
        </div>
        <div className="divide-y divide-white/[0.04]">
          {GUIDES.map((g) => {
            const Icon = g.icon;
            return (
              <div key={g.title} className="flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors cursor-default">
                <div className="flex items-center gap-3">
                  <Icon size={14} className="text-white/30 flex-shrink-0" />
                  <div>
                    <div className="text-[12.5px] font-medium">{g.title}</div>
                    <div className="text-[11px] text-white/35">{g.desc}</div>
                  </div>
                </div>
                <span className="text-[10px] text-white/20 bg-white/[0.04] px-2 py-0.5 rounded">Coming soon</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* FAQs */}
      <div>
        <h2 className="text-[12.5px] font-semibold mb-2.5">Frequently asked questions</h2>
        <div className="space-y-2">
          {FAQS.map((faq) => (
            <details key={faq.q} className="card group">
              <summary className="px-4 py-3 text-[12.5px] font-medium cursor-pointer list-none flex items-center justify-between hover:text-white transition-colors">
                {faq.q}
                <span className="text-white/20 text-lg leading-none group-open:rotate-45 transition-transform inline-block">+</span>
              </summary>
              <div className="px-4 pb-3 text-[12px] text-white/50 leading-relaxed border-t border-white/[0.04] pt-2.5">
                {faq.a}
              </div>
            </details>
          ))}
        </div>
      </div>

      {/* Legal links */}
      <div className="flex items-center gap-4 text-[11.5px] text-white/25 pt-2">
        <a href="/terms" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-white/50 transition-colors">
          Terms of Service <ExternalLink size={10} />
        </a>
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-white/50 transition-colors">
          Privacy Policy <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}
